import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, FormControl, InputLabel, Select, MenuItem,
    FormHelperText, FormGroup, FormControlLabel, Checkbox,
    CircularProgress, Alert, Grid, DialogContentText
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { HotelService } from '../api/services/hotelService';
import { useSelector } from 'react-redux';

const SETTINGS_OPTIONS = [
    { label: 'Check-In/Out Policies', value: 'CheckInOutPolicy' },
    { label: 'Taxes & Fee Rules', value: 'TaxFeeRules' },
    { label: 'Booking Sources', value: 'BookingSources' },
    { label: 'Deposit & Cancellation Policies', value: 'DepositCancellationPolicy' },
    { label: 'Accepted Payment Methods', value: 'PaymentMethods' },
    { label: 'Automated Email Templates', value: 'EmailTemplates' }
];

export default function CloneHotelSettingsModal({ open, onClose, setNotification }) {
    const hotels = useSelector(state => state.hotels.list) || [];
    
    const [sourceHotelId, setSourceHotelId] = useState('');
    const [targetHotelId, setTargetHotelId] = useState('');
    const [selectedSettings, setSelectedSettings] = useState([]);
    const [error, setError] = useState(null);
    const [isCloning, setIsCloning] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        if (open) {
            setSourceHotelId('');
            setTargetHotelId('');
            setSelectedSettings([]);
            setError(null);
            setIsCloning(false);
            setConfirmOpen(false);
        }
    }, [open]);

    const handleToggleSetting = (value) => {
        setSelectedSettings(prev =>
            prev.includes(value)
                ? prev.filter(item => item !== value)
                : [...prev, value]
        );
    };

    const validateForm = () => {
        if (!sourceHotelId) return 'Please select a source property.';
        if (!targetHotelId) return 'Please select a target property.';
        if (sourceHotelId === targetHotelId) return 'Source and target properties cannot be the same.';
        if (selectedSettings.length === 0) return 'Please select at least one setting to clone.';
        return null;
    };

    const handlePreSubmit = () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setConfirmOpen(true);
    };

    const handleCloneSettings = async () => {
        setConfirmOpen(false);
        setIsCloning(true);
        setError(null);
        try {
            const tenantId = JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1';
            const payload = {
                sourceHotelId,
                targetHotelId,
                cloneSettings: selectedSettings
            };
            await HotelService.cloneHotelSettings(tenantId, payload);
            setNotification({ open: true, message: 'Hotel configuration modules cloned successfully!', severity: 'success' });
            onClose();
        } catch (err) {
            console.error('Clone settings error:', err);
            setNotification({
                open: true,
                message: err?.response?.data?.message || err.message || 'Failed to clone hotel settings',
                severity: 'error'
            });
        } finally {
            setIsCloning(false);
        }
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={!isCloning ? onClose : undefined} 
                maxWidth="md" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ContentCopyIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">Clone Hotel Settings</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Use this toolkit to rapidly copy configuration modules from one property to another.
                        </Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth disabled={isCloning}>
                                <InputLabel>Source Property</InputLabel>
                                <Select
                                    value={sourceHotelId}
                                    label="Source Property"
                                    onChange={(e) => {
                                        setSourceHotelId(e.target.value);
                                        setError(null);
                                    }}
                                >
                                    {hotels.map(h => (
                                        <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>Select the hotel to copy settings FROM</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth disabled={isCloning}>
                                <InputLabel>Target Property</InputLabel>
                                <Select
                                    value={targetHotelId}
                                    label="Target Property"
                                    onChange={(e) => {
                                        setTargetHotelId(e.target.value);
                                        setError(null);
                                    }}
                                >
                                    {hotels.map(h => (
                                        <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>Select the hotel to copy settings TO</FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            Select Modules to Clone
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            The selected configurations will overwrite the existing rules in the target property.
                        </Typography>
                        <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            {SETTINGS_OPTIONS.map((opt) => (
                                <FormControlLabel
                                    key={opt.value}
                                    control={
                                        <Checkbox 
                                            checked={selectedSettings.includes(opt.value)} 
                                            onChange={() => handleToggleSetting(opt.value)}
                                            disabled={isCloning}
                                        />
                                    }
                                    label={opt.label}
                                />
                            ))}
                        </FormGroup>
                    </Box>

                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={onClose} variant="outlined" disabled={isCloning} sx={{ borderRadius: 2 }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handlePreSubmit} 
                        variant="contained" 
                        color="primary"
                        disabled={isCloning}
                        startIcon={isCloning ? <CircularProgress size={20} color="inherit" /> : <ContentCopyIcon />}
                        sx={{ borderRadius: 2 }}
                    >
                        {isCloning ? 'Processing...' : 'Clone Settings'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog 
                open={confirmOpen} 
                onClose={() => setConfirmOpen(false)}
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle fontWeight="bold" color="error">Confirm Settings Override</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'text.primary' }}>
                        Warning: This operation will copy the selected configuration layers directly into your destination property, completely overriding existing rules. This action cannot be undone. Are you sure you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleCloneSettings} variant="contained" color="error" sx={{ borderRadius: 2 }}>
                        Yes, Proceed
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
